import { Box, Button, Typography, styled } from '@mui/material';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { CLIENTS_ROUTE, PAYMENT_INFO_ROUTE } from '../../utils/routes';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchCoachRequest, PaymentStatus } from '../../redux/coach/coachSlice';
import { onboardingActions } from '../../redux/onboarding/onboardingSlice';
import { formatPlanName, formatPrice, getPlanRank } from '../../utils/paymentUtills';
import { SubscriptionPlanTypes } from '../../types/coach';
import StyledBackButton from '../Common/StyledBackButton';

const StyledCurrentPlanButton = styled(Button)(() => ({
  padding: '12px 16px !important',
  gap: '10px',
  borderRadius: '15px',
  fontFamily: 'Satoshi',
  fontSize: '15px',
  fontWeight: 700,
  lineHeight: '20px ',
  backgroundColor: '#2A3C67',
  textTransform: 'none',
}));

const StyledUpgradeButton = styled(Button)(() => ({
  background: 'var(--Brand-Cadet-Blue, linear-gradient(138deg, #6599A5 24.11%, #3B667E 83.2%))',
  borderRadius: '15px',
  '&:hover': {
    background: 'var(--Brand-Cadet-Blue, linear-gradient(138deg, #4e7c91 24.11%, #2d4f5f 83.2%))',
  },
}));

interface ISubscriptionPlanCardProps {
  plan: {
    id: string;
    name: SubscriptionPlanTypes;
    price: number;
    rank: number;
    features: string[];
  };
  currentPlan: SubscriptionPlanTypes;
  handlePaymentNavigate: (_value: SubscriptionPlanTypes) => void;
  handleDowngrade: (_value: SubscriptionPlanTypes) => void;
}

export default function SubscriptionPlanCardInManage({
  plan,
  currentPlan,
  handlePaymentNavigate,
  handleDowngrade,
}: Readonly<ISubscriptionPlanCardProps>) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { coach } = useAppSelector((state) => state.coach);
  const { isFreeTrialActivated } = useAppSelector((state) => state.subscription);
  const nextPlan = coach.subscriptionPlanStatus?.nextPlan || SubscriptionPlanTypes.FREE;
  const paymentStatus = coach.subscriptionPlanStatus?.paymentStatus;
  const currentPlanRank = getPlanRank(currentPlan);
  const nextPlanRank = getPlanRank(nextPlan);
  const diff = nextPlanRank - currentPlanRank;

  const hasDowngraded =
    diff < 0 &&
    ((nextPlan === SubscriptionPlanTypes.FREE && paymentStatus === PaymentStatus.PENDING) ||
      nextPlan !== SubscriptionPlanTypes.FREE);

  const hasUpgraded = diff > 0;

  const { pkgPrice, period } = formatPrice(plan.price, plan.name);

  const handlePaymentInfoNavigate = () => {
    navigate({
      pathname: PAYMENT_INFO_ROUTE,
      search: createSearchParams({ plan: plan.name }).toString(),
    });
  };

  useEffect(() => {
    if (isFreeTrialActivated && coach.id) {
      dispatch(fetchCoachRequest());
      navigate(CLIENTS_ROUTE);
      dispatch(onboardingActions.openOnboardingSuccessModal());
    }
  }, [isFreeTrialActivated, coach.id, dispatch, navigate]);

  const renderPlanButton = (isCurrentPlan: boolean) => {
    if (isCurrentPlan) {
      return (
        <StyledCurrentPlanButton variant="contained" key={currentPlanRank}>
          {t('subscriptionPlans.current')}
        </StyledCurrentPlanButton>
      );
    }
    if (currentPlanRank > 0 && plan.name === nextPlan && hasDowngraded) {
      return (
        <StyledBackButton key={plan.name} sx={{ height: '44px' }}>
          <span>{t('subscriptionPlans.downgraded')}</span>
        </StyledBackButton>
      );
    }
    if (currentPlanRank > 0 && plan.name === nextPlan && hasUpgraded) {
      return (
        <StyledBackButton key={plan.name} sx={{ height: '44px' }}>
          <span>{t('subscriptionPlans.upgraded')} </span>
        </StyledBackButton>
      );
    }

    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }} key={currentPlanRank}>
        {plan.rank > currentPlanRank ? (
          <StyledUpgradeButton onClick={() => handlePaymentNavigate(plan.name)} variant="contained">
            {t('subscriptionPlans.upgrade')}
          </StyledUpgradeButton>
        ) : (
          <StyledBackButton onClick={() => handleDowngrade(plan.name)} sx={{ height: '44px' }}>
            <span>{t('subscriptionPlans.downgrade')}</span>
          </StyledBackButton>
        )}
      </Box>
    );
  };

  const renderPlanStatus = () => {
    if (currentPlan === plan.name && currentPlanRank > 0) {
      if (hasDowngraded) {
        return <span>{t('payment.cancelledPlan', { plan: formatPlanName(nextPlan) })}</span>;
      }
      if (hasUpgraded) {
        return <span>{t('payment.upgradedPlan', { plan: formatPlanName(nextPlan) })}</span>;
      }
    }
    return renderPlanButton(currentPlan === plan.name);
  };

  return (
    <Box
      key={plan.id}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        padding: 3.5,
        borderRadius: 3,
        boxShadow: '0px 4px 40px 0px #FFDDC840',
        backgroundColor: '#FFFFFF',
        minHeight: 280,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          alignItems: 'flex-start',
        }}
      >
        <Typography
          variant="bodyBold"
          color="primary"
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          {formatPlanName(plan.name)}
          <Box onClick={handlePaymentInfoNavigate}>
            <img
              src="/assets/icons/question-circle.svg"
              alt="question-circle"
              style={{ cursor: 'pointer' }}
            />
          </Box>
        </Typography>
        <Box display="flex" alignItems="baseline" gap={0.5}>
          <Typography variant="title2Bold" color="primary" fontWeight="bold">
            {pkgPrice}
          </Typography>
          {period && (
            <Typography variant="meta" color="primary">
              {period}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Features Section */}
      <Box
        sx={{
          display: 'grid',
          gap: 1,
        }}
      >
        {plan.features.map((feature) => (
          <Typography
            key={feature}
            variant="calloutRegular"
            fontWeight={400}
            component="div"
            sx={{ display: 'flex', alignItems: 'flex-start', color: 'primary' }}
          >
            <Box
              component="span"
              sx={{
                width: '1rem',
                display: 'flex',
                justifyContent: 'flex-start',
              }}
            >
              &#8226;
            </Box>
            <Box
              component="span"
              sx={{
                flex: 1,
                wordBreak: 'break-word',
              }}
            >
              {t(`subscriptionPlans.features.${feature}`)}
            </Box>
          </Typography>
        ))}
      </Box>

      {/* Plan Status */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          width: '100%',
          fontWeight: 450,
        }}
      >
        {renderPlanStatus()}
      </Box>
    </Box>
  );
}
